import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { downloadStoredFile } from "@/lib/storage-url";
import { useSignedStoredUrl } from "@/hooks/useSignedStoredUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, FileText, PenLine, X } from "lucide-react";
import TemplateFieldEditor, { type PlacedField } from "./TemplateFieldEditor";
import { SignatureDialog } from "@/components/tenant/SignatureDialog";
import {
  TemplateSectionCard,
  TemplateLoading,
  TemplateEmptyState,
  TemplateFieldReference,
  TemplateRowActions,
} from "./templates/TemplateSectionUI";
import { PdfUploadField } from "./templates/PdfUploadField";
import { useLanguage } from "@/contexts/LanguageContext";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { formatDateNumeric } from "@/lib/date-format";

// ── Owner-onboarding field names ─────────────────────────────
const OWNER_FIELDS = [
  "OWNER_NAME",
  "OWNER_EMAIL",
  "OWNER_PHONE",
  "OWNER_ADDRESS",
  "OWNER_IBAN",
  "OWNER_BIC",
  "OWNER_BANK_HOLDER",
  "PROPERTY_ADDRESS",
  "PROPERTY_NAME",
  // One key per printed slot on the MULTI Verwaltervollmacht (3 columns x 5
  // rows). Each is placed at its own coordinates because the filler stamps
  // fields at fixed positions — there is no repeating-field type to flow a
  // list into. Slots beyond the objects an invite actually covers stamp
  // nothing, so a template with all 15 placed is safe for a 1-object invite.
  ...(Array.from(
    { length: 15 },
    (_, i) => `PROPERTY_${i + 1}_ADDRESS`
  ) as string[]),
  "FEE_AMOUNT",
  "FEE_MODEL",
  "SIGNING_DATE",
  "OWNER_SIGNING_CITY",
  "OWNER_SIGNATURE",
  "COLIVIQ_SIGNATURE",
  "REFERENCE_NUMBER",
] as const;

const SAMPLE_DATA: Record<string, string> = {
  OWNER_NAME: "Hans Mustermann",
  OWNER_EMAIL: "hans@example.com",
  OWNER_PHONE: "+49 170 9876543",
  OWNER_ADDRESS: "Musterstraße 12, 10115 Berlin",
  OWNER_IBAN: "DE89 3704 0044 0532 0130 00",
  OWNER_BIC: "COBADEFFXXX",
  OWNER_BANK_HOLDER: "Hans Mustermann",
  PROPERTY_ADDRESS: "Hauptstraße 5, 80333 München",
  ...Object.fromEntries(
    Array.from({ length: 15 }, (_, i) => [
      `PROPERTY_${i + 1}_ADDRESS`,
      i < 3 ? `Liegenschaft ${i + 1}, 80333 München` : "",
    ])
  ),
  PROPERTY_NAME: "Stadtmitte WG",
  FEE_AMOUNT: "120.00",
  FEE_MODEL: "Flat fee per month",
  SIGNING_DATE: "01.06.2026",
  OWNER_SIGNING_CITY: "Berlin",
  OWNER_SIGNATURE: "[Owner signature]",
  COLIVIQ_SIGNATURE: "[coliviq signature]",
  REFERENCE_NUMBER: "REF-2026-001",
};

type TemplateKind = "sign" | "attach";

type OwnerContractTemplate = {
  id: string;
  name: string;
  description: string | null;
  pdf_url: string | null;
  pdf_file_name: string | null;
  coliviq_signature_url: string | null;
  is_default: boolean;
  kind: TemplateKind;
  created_at: string;
  updated_at: string;
  vector_overlay_config: { fields: PlacedField[] } | null;
};

export function OwnerContractTemplates() {
  const { t, language: activeLanguage } = useLanguage();
  // COL-478: this surface still carries inline de/en copy (swept in COL-479);
  // tenants in the four new languages read the English half.
  const language = baseLanguageFor(activeLanguage);
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<OwnerContractTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templatePendingDelete, setTemplatePendingDelete] =
    useState<OwnerContractTemplate | null>(null);

  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    pdf_url: "",
    pdf_file_name: "",
    coliviq_signature_url: "",
    kind: "sign" as TemplateKind,
  });

  const [rawPdfBytes, setRawPdfBytes] = useState<Uint8Array | null>(null);
  // The signature preview is an <img>; its src is a signed URL, never the
  // stored public one (COL-436).
  const coliviqSignature = useSignedStoredUrl(
    formData.coliviq_signature_url || null,
    "contracts"
  );
  const [placedFields, setPlacedFields] = useState<PlacedField[]>([]);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);

  useEffect(() => {
    if (!dialogOpen || rawPdfBytes || !formData.pdf_url) return;
    let cancelled = false;
    (async () => {
      try {
        // Through the storage API, not the stored public URL: the contracts
        // bucket is going private (COL-436).
        const blob = await downloadStoredFile(formData.pdf_url, "contracts");
        if (cancelled) return;
        const bytes = new Uint8Array(await blob.arrayBuffer());
        if (cancelled) return;
        setRawPdfBytes(bytes);
      } catch {
        // user can re-upload
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, formData.pdf_url]);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["owner-contract-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("owner_contract_templates")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      // vector_overlay_config is jsonb; OwnerContractTemplate asserts its shape.
      return data as unknown as OwnerContractTemplate[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: {
      id?: string;
      name: string;
      description: string | null;
      pdf_url: string | null;
      pdf_file_name: string | null;
      coliviq_signature_url: string | null;
      kind: TemplateKind;
      vector_overlay_config?: { fields: PlacedField[] } | null;
    }) => {
      const row = {
        name: payload.name,
        description: payload.description,
        pdf_url: payload.pdf_url,
        pdf_file_name: payload.pdf_file_name,
        // Attachments carry no signature / field overlay.
        coliviq_signature_url:
          payload.kind === "attach" ? null : payload.coliviq_signature_url,
        kind: payload.kind,
        vector_overlay_config:
          payload.kind === "attach"
            ? null
            : (payload.vector_overlay_config ?? null),
        updated_at: new Date().toISOString(),
      };
      if (payload.id) {
        const { error } = await supabase
          .from("owner_contract_templates")
          .update(row)
          .eq("id", payload.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("owner_contract_templates")
          .insert(row);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owner-contract-templates"] });
      toast.success(
        editingTemplate
          ? t("admin.ownerTemplates.toastUpdated", "Template updated")
          : t("admin.ownerTemplates.toastCreated", "Template created")
      );
      closeDialog();
    },
    onError: (err: Error) =>
      toast.error(
        `${t("admin.ownerTemplates.saveFailed", "Failed to save")}: ${err.message}`
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("owner_contract_templates")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owner-contract-templates"] });
      toast.success(t("admin.ownerTemplates.toastDeleted", "Template deleted"));
    },
    onError: (err: Error) =>
      toast.error(
        `${t("admin.ownerTemplates.deleteFailed", "Failed to delete")}: ${err.message}`
      ),
  });

  function resetState() {
    setRawPdfBytes(null);
    setPlacedFields([]);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingTemplate(null);
    resetState();
    setFormData({
      name: "",
      description: "",
      pdf_url: "",
      pdf_file_name: "",
      coliviq_signature_url: "",
      kind: "sign",
    });
  }

  function openCreate() {
    setEditingTemplate(null);
    resetState();
    setFormData({
      name: "",
      description: "",
      pdf_url: "",
      pdf_file_name: "",
      coliviq_signature_url: "",
      kind: "sign",
    });
    setDialogOpen(true);
  }

  function openEdit(tpl: OwnerContractTemplate) {
    setEditingTemplate(tpl);
    resetState();
    if (tpl.vector_overlay_config?.fields)
      setPlacedFields(tpl.vector_overlay_config.fields);
    setFormData({
      name: tpl.name,
      description: tpl.description ?? "",
      pdf_url: tpl.pdf_url ?? "",
      pdf_file_name: tpl.pdf_file_name ?? "",
      coliviq_signature_url: tpl.coliviq_signature_url ?? "",
      kind: tpl.kind ?? "sign",
    });
    setDialogOpen(true);
  }

  async function handleFileUpload(file: File) {
    setUploading(true);
    resetState();
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      setRawPdfBytes(bytes);

      const fileName = `owner-contract-templates/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("contracts")
        .upload(fileName, file, {
          contentType: "application/pdf",
          upsert: false,
        });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(fileName);
      setFormData((prev) => ({
        ...prev,
        pdf_url: urlData.publicUrl,
        pdf_file_name: file.name,
      }));
      toast.success(t("admin.ownerTemplates.toastPdfUploaded", "PDF uploaded"));
    } catch (err: any) {
      toast.error(
        `${t("admin.ownerTemplates.uploadFailed", "Upload failed")}: ${err.message}`
      );
    } finally {
      setUploading(false);
    }
  }

  async function handleSignatureConfirm(pngBlob: Blob) {
    setUploadingSignature(true);
    try {
      const seg = editingTemplate?.id ?? "new";
      const fileName = `owner-template-signatures/${seg}/${Date.now()}.png`;
      const { error } = await supabase.storage
        .from("contracts")
        .upload(fileName, pngBlob, { contentType: "image/png", upsert: false });
      if (error) throw error;
      const { data: urlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(fileName);
      setFormData((prev) => ({
        ...prev,
        coliviq_signature_url: urlData.publicUrl,
      }));
      setSignatureDialogOpen(false);
      toast.success(
        t(
          "admin.ownerTemplates.toastSignatureSaved",
          "Signature saved. Click Update/Create to persist."
        )
      );
    } catch (err: any) {
      toast.error(
        err?.message ||
          t(
            "admin.ownerTemplates.toastSignatureFailed",
            "Failed to upload signature."
          )
      );
    } finally {
      setUploadingSignature(false);
    }
  }

  function handleSubmit() {
    if (!formData.name.trim()) {
      toast.error(
        t("admin.ownerTemplates.errNameRequired", "Template name is required")
      );
      return;
    }
    if (!formData.pdf_url.trim()) {
      toast.error(
        t("admin.ownerTemplates.errPdfRequired", "Template PDF is required")
      );
      return;
    }
    saveMutation.mutate({
      id: editingTemplate?.id,
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      pdf_url: formData.pdf_url.trim(),
      pdf_file_name: formData.pdf_file_name.trim() || null,
      coliviq_signature_url: formData.coliviq_signature_url.trim() || null,
      kind: formData.kind,
      vector_overlay_config:
        placedFields.length > 0 ? { fields: placedFields } : null,
    });
  }

  // The field editor / PDF preview only renders for signable templates, so the
  // wide two-column dialog should only kick in then — otherwise an "attach"
  // template (e.g. AGB) auto-loads its PDF and leaves a big empty right pane.
  const showEditor = !!rawPdfBytes && formData.kind === "sign";

  return (
    <>
      <TemplateSectionCard
        icon={FileText}
        title={t(
          "admin.ownerTemplates.sectionTitle",
          "Owner Contract Templates"
        )}
        description={t(
          "admin.ownerTemplates.sectionDesc",
          "PDF templates used during owner onboarding. Fields are stamped at drag-and-drop positions (OWNER_NAME, OWNER_IBAN, OWNER_SIGNATURE, etc.)."
        )}
        onAdd={openCreate}
      >
        {isLoading ? (
          <TemplateLoading />
        ) : templates.length === 0 ? (
          <TemplateEmptyState
            icon={FileText}
            title={t(
              "admin.ownerTemplates.emptyTitle",
              "No owner templates yet. Add your first owner contract template."
            )}
            hint={t(
              "admin.ownerTemplates.emptyDesc",
              "This template is sent to owners during the onboarding flow."
            )}
          />
        ) : (
          <div className="overflow-hidden rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead>
                    {t("admin.ownerTemplates.colName", "Name")}
                  </TableHead>
                  <TableHead>
                    {t("admin.ownerTemplates.colCreated", "Created")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("admin.ownerTemplates.colActions", "Actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow key={tpl.id}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{tpl.name}</span>
                        {tpl.description && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {tpl.description}
                          </p>
                        )}
                        {tpl.pdf_file_name && (
                          <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                            {tpl.pdf_file_name}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDateNumeric(tpl.created_at, language)}
                    </TableCell>
                    <TableCell className="text-right">
                      <TemplateRowActions
                        pdfUrl={tpl.pdf_url}
                        downloadName={tpl.pdf_file_name ?? `${tpl.name}.pdf`}
                        onEdit={() => openEdit(tpl)}
                        onDelete={() => {
                          setTemplatePendingDelete(tpl);
                          setDeleteDialogOpen(true);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <TemplateFieldReference
          title={t("admin.ownerTemplates.fieldNamesTitle", "Owner Field Names")}
          description={t(
            "admin.ownerTemplates.fieldNamesDesc",
            "Drag these onto the PDF to define where each value is stamped:"
          )}
          fields={OWNER_FIELDS}
        />
      </TemplateSectionCard>

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent
          className={
            showEditor ? "max-w-6xl max-h-[92vh] overflow-y-auto" : "max-w-lg"
          }
        >
          <DialogHeader>
            <DialogTitle>
              {editingTemplate
                ? t(
                    "admin.ownerTemplates.dialogEditTitle",
                    "Edit Owner Template"
                  )
                : t(
                    "admin.ownerTemplates.dialogNewTitle",
                    "New Owner Contract Template"
                  )}
            </DialogTitle>
          </DialogHeader>

          <div className={showEditor ? "grid grid-cols-[320px_1fr] gap-6" : ""}>
            <div className="space-y-4 mt-1">
              <div className="space-y-1.5">
                <Label htmlFor="oct-name">
                  {t("admin.ownerTemplates.fieldName", "Template Name *")}
                </Label>
                <Input
                  id="oct-name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder={t(
                    "admin.ownerTemplates.namePlaceholder",
                    "e.g. Verwaltungsvertrag 2026"
                  )}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="oct-desc">
                  {t("admin.ownerTemplates.fieldDescription", "Description")}
                </Label>
                <Textarea
                  id="oct-desc"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder={t(
                    "admin.ownerTemplates.descPlaceholder",
                    "Optional notes"
                  )}
                  rows={2}
                />
              </div>

              <div className="space-y-1.5">
                <Label>
                  {t("admin.ownerTemplates.fieldDocType", "Document type")}
                </Label>
                <div className="grid grid-cols-2 gap-1 rounded-lg border bg-muted/40 p-1">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, kind: "sign" })}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      formData.kind === "sign"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t(
                      "admin.ownerTemplates.docTypeSign",
                      "Sign (stamped + signed)"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, kind: "attach" })}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      formData.kind === "attach"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t(
                      "admin.ownerTemplates.docTypeAttach",
                      "Attach (as-is, e.g. AGB)"
                    )}
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {formData.kind === "attach"
                    ? t(
                        "admin.ownerTemplates.docTypeAttachHint",
                        "Attached to the confirmation email as-is — no fields, no signature."
                      )
                    : t(
                        "admin.ownerTemplates.docTypeSignHint",
                        "Stamped with owner fields + signature during onboarding."
                      )}
                </p>
              </div>

              <PdfUploadField
                value={formData.pdf_url}
                onValueChange={(v) => setFormData({ ...formData, pdf_url: v })}
                onFile={handleFileUpload}
                uploading={uploading}
                loadingExisting={!!formData.pdf_url && !rawPdfBytes}
                fileName={formData.pdf_file_name || undefined}
              />

              {formData.kind === "sign" && (
                <p className="text-[11px] text-muted-foreground">
                  {t(
                    "admin.ownerTemplates.dragHint",
                    "Drag owner fields onto the PDF preview to position them."
                  )}
                </p>
              )}

              {/* CoLiviq signature — only for signable templates */}
              {formData.kind === "sign" && (
                <div className="space-y-1.5">
                  <Label>
                    {t(
                      "admin.ownerTemplates.coliviqSignature",
                      "coliviq Signature"
                    )}
                  </Label>
                  {formData.coliviq_signature_url ? (
                    <div className="rounded-lg border bg-muted/20 p-2 flex items-center gap-2">
                      {coliviqSignature.failed ? (
                        <span className="text-xs text-destructive">
                          {t(
                            "admin.templates.signatureLoadFailed",
                            "Could not load the signature image."
                          )}
                        </span>
                      ) : (
                        <img
                          src={coliviqSignature.url ?? undefined}
                          alt="coliviq signature"
                          className="h-14 w-auto max-w-[160px] rounded-md bg-white border object-contain"
                        />
                      )}
                      <div className="flex-1 flex flex-col gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setSignatureDialogOpen(true)}
                          disabled={uploadingSignature}
                        >
                          <PenLine className="w-3 h-3 mr-1" />
                          {t("admin.ownerTemplates.replace", "Replace")}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-destructive hover:text-destructive"
                          onClick={() =>
                            setFormData((p) => ({
                              ...p,
                              coliviq_signature_url: "",
                            }))
                          }
                          disabled={uploadingSignature}
                        >
                          <X className="w-3 h-3 mr-1" />
                          {t("admin.ownerTemplates.remove", "Remove")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setSignatureDialogOpen(true)}
                      disabled={uploadingSignature}
                    >
                      {uploadingSignature ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <PenLine className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {t(
                        "admin.ownerTemplates.drawOrUploadSignature",
                        "Draw or upload coliviq signature"
                      )}
                    </Button>
                  )}
                  <p className="text-[11px] text-muted-foreground">
                    {t(
                      "admin.ownerTemplates.signatureStampHint",
                      "Stamped at COLIVIQ_SIGNATURE position in the generated PDF."
                    )}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={closeDialog}>
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending && (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  )}
                  {editingTemplate
                    ? t("common.update", "Update")
                    : t("common.create", "Create")}
                  {placedFields.length > 0 && (
                    <span className="ml-1 text-xs opacity-70">
                      ({placedFields.length}{" "}
                      {t("admin.ownerTemplates.fieldsCount", "fields")})
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {rawPdfBytes && formData.kind === "sign" && (
              <div className="flex flex-col min-h-0">
                <TemplateFieldEditor
                  pdfBytes={rawPdfBytes}
                  initialFields={placedFields}
                  onChange={setPlacedFields}
                  fieldNames={OWNER_FIELDS as unknown as string[]}
                  sampleData={SAMPLE_DATA}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialogOpen(false);
            setTemplatePendingDelete(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("admin.ownerTemplates.deleteTitle", "Delete template?")}
            </DialogTitle>
            <DialogDescription>
              {templatePendingDelete
                ? `${t("admin.ownerTemplates.deleteBodyPrefix", "This will permanently delete")} "${templatePendingDelete.name}".`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setTemplatePendingDelete(null);
              }}
              disabled={deleteMutation.isPending}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                templatePendingDelete &&
                deleteMutation.mutate(templatePendingDelete.id, {
                  onSettled: () => {
                    setDeleteDialogOpen(false);
                    setTemplatePendingDelete(null);
                  },
                })
              }
              disabled={!templatePendingDelete || deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              )}
              {t("common.delete", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignatureDialog
        open={signatureDialogOpen}
        onOpenChange={setSignatureDialogOpen}
        onConfirm={handleSignatureConfirm}
        submitting={uploadingSignature}
        language={language}
      />
    </>
  );
}
